import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HeaderBar from '../components/HeaderBar';
import './ManageBookingsPage.css';

const DEFAULT_TIME_SLOTS = [
    { label: 'Full Day (07:00 - 17:00)', start: '07:00', end: '17:00' },
    { label: 'Morning (07:00 - 12:00)', start: '07:00', end: '12:00' },
    { label: 'Afternoon (12:00 - 17:00)', start: '12:00', end: '17:00' },
];

function ManageBookingsPage({ user, onLogout }) {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [meetingRoomBookings, setMeetingRoomBookings] = useState([]);
    const [desks, setDesks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [newBooking, setNewBooking] = useState({
        desk_id: '',
        booking_date: '',
        start_time: DEFAULT_TIME_SLOTS[0].start,
        end_time: DEFAULT_TIME_SLOTS[0].end,
    });
    const [editDraft, setEditDraft] = useState(null);
    const [deskAvailability, setDeskAvailability] = useState([]);
    const [editMeetingDraft, setEditMeetingDraft] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ show: false, bookingId: null, message: '' });

    const today = useMemo(() => new Date().toISOString().split('T')[0], []);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchBookings = useCallback(async () => {
        try {
            const response = await axios.get(`/api/bookings/user/${user.email}`);
            setBookings(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load bookings');
        }
    }, [user.email]);

    const fetchMeetingRoomBookings = useCallback(async () => {
        try {
            const response = await axios.get(`/api/meeting-rooms/user/${user.email}`);
            console.log('[DEBUG] Meeting room bookings fetched:', response.data);
            setMeetingRoomBookings(response.data || []);
        } catch (err) {
            console.error('Failed to load meeting room bookings:', err);
            setError(err.response?.data?.error || 'Failed to load meeting room bookings');
        }
    }, [user.email]);

    const fetchDesks = useCallback(async () => {
        if (!user.unit) return;
        try {
            const response = await axios.get(`/api/desks/unit/${user.unit}`);
            setDesks(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load desks');
        }
    }, [user.unit]);

    useEffect(() => {
        const bootstrap = async () => {
            setLoading(true);
            setError('');
            await Promise.all([fetchBookings(), fetchMeetingRoomBookings(), fetchDesks()]);
            setLoading(false);
        };
        bootstrap();

        // Poll every 20s for auto-updates
        const pollInterval = setInterval(() => {
            fetchBookings();
            fetchMeetingRoomBookings();
        }, 20000);

        // Refresh on window focus
        const handleFocus = () => {
            fetchBookings();
            fetchMeetingRoomBookings();
            fetchDesks();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(pollInterval);
            window.removeEventListener('focus', handleFocus);
        };
    }, [fetchBookings, fetchMeetingRoomBookings, fetchDesks]);

    const handleCreateBooking = async (event) => {
        event.preventDefault();
        if (!newBooking.desk_id || !newBooking.booking_date) {
            setError('Please choose a desk and date for the new booking');
            return;
        }

        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            await axios.post('/api/bookings', {
                desk_id: newBooking.desk_id,
                user_email: user.email,
                booking_date: newBooking.booking_date,
                start_time: newBooking.start_time,
                end_time: newBooking.end_time,
            });
            setSuccess('Booking created successfully');
            setNewBooking({
                desk_id: '',
                booking_date: '',
                start_time: DEFAULT_TIME_SLOTS[0].start,
                end_time: DEFAULT_TIME_SLOTS[0].end,
            });
            await fetchBookings();
        } catch (err) {
            setError(err.response?.data?.error || 'Unable to create booking');
        } finally {
            setProcessing(false);
        }
    };

    const openEditModal = (booking) => {
        setEditDraft({
            id: booking.id,
            desk_id: booking.desk_id,
            booking_date: booking.booking_date,
            start_time: booking.start_time || DEFAULT_TIME_SLOTS[0].start,
            end_time: booking.end_time || DEFAULT_TIME_SLOTS[0].end,
        });
        setError('');
        setSuccess('');
    };

    // Fetch desk availability when editing booking
    useEffect(() => {
        if (!editDraft || !user.unit) return;

        const fetchAvailability = async () => {
            try {
                const params = new URLSearchParams({
                    unit: user.unit,
                    date: editDraft.booking_date,
                    start: editDraft.start_time || '',
                    end: editDraft.end_time || ''
                });

                const response = await axios.get(`/api/desks/available?${params}`);
                setDeskAvailability(response.data || []);
            } catch (err) {
                console.error('Failed to fetch desk availability:', err);
                setDeskAvailability(desks); // Fallback to all desks
            }
        };

        fetchAvailability();
    }, [editDraft?.booking_date, editDraft?.start_time, editDraft?.end_time, user.unit, desks, editDraft]);

    const handleEditSubmit = async (event) => {
        event.preventDefault();
        if (!editDraft) return;
        if (!editDraft.desk_id || !editDraft.booking_date) {
            setError('All fields are required for the update');
            return;
        }

        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            await axios.put(`/api/bookings/${editDraft.id}`, {
                desk_id: editDraft.desk_id,
                booking_date: editDraft.booking_date,
                start_time: editDraft.start_time,
                end_time: editDraft.end_time,
            });
            setSuccess('Booking updated successfully');
            setEditDraft(null);
            await fetchBookings();
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Unable to update booking';
            const conflict = err.response?.data?.conflict;

            if (conflict) {
                setError(`${errorMsg}: ${conflict.user} has this desk ${conflict.time}`);
            } else {
                setError(errorMsg);
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteBooking = async (bookingId) => {
        if (!window.confirm('Cancel this booking?')) {
            return;
        }

        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            await axios.delete(`/api/bookings/${bookingId}`);
            setSuccess('Booking cancelled');
            await fetchBookings();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to cancel booking');
        } finally {
            setProcessing(false);
        }
    };

    const handleCancelMeetingRoom = async (bookingId) => {
        console.log('[DEBUG] Opening confirmation dialog for booking:', bookingId);
        setConfirmDialog({ show: true, bookingId, message: 'Are you sure you want to cancel this meeting room booking?' });
    };

    const confirmCancelMeetingRoom = async () => {
        const { bookingId } = confirmDialog;
        setConfirmDialog({ show: false, bookingId: null, message: '' });

        if (!bookingId) return;

        console.log('[DEBUG] Confirming cancellation for booking:', bookingId);
        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            console.log('[DEBUG] Sending DELETE request to /api/meeting-rooms/bookings/' + bookingId);
            const response = await axios.delete(`/api/meeting-rooms/bookings/${bookingId}`);
            console.log('[DEBUG] DELETE response:', response);
            setSuccess('Meeting room booking cancelled');
            console.log('[DEBUG] Calling fetchMeetingRoomBookings()');
            await fetchMeetingRoomBookings();
        } catch (err) {
            console.error('[DEBUG] DELETE request failed:', err);
            console.error('[DEBUG] Error details:', err.response?.data || err.message);
            setError(err.response?.data?.error || 'Failed to cancel meeting room booking');
        } finally {
            console.log('[DEBUG] Setting processing to false');
            setProcessing(false);
        }
    };

    const openEditMeetingModal = (booking) => {
        console.log('[DEBUG] Opening edit modal for booking:', booking);
        setEditMeetingDraft({
            id: booking.id,
            room_name: booking.room_name || booking.booth_id,
            booking_date: booking.booking_date,
            start_time: booking.start_time,
            end_time: booking.end_time,
            meeting_title: booking.meeting_title || '',
            original_duration_minutes: (() => {
                const [sh, sm] = booking.start_time.split(':').map(Number);
                const [eh, em] = booking.end_time.split(':').map(Number);
                return (eh * 60 + em) - (sh * 60 + sm);
            })()
        });
        setError('');
        setSuccess('');
    };

    const handleEditMeetingSubmit = async (event) => {
        event.preventDefault();
        if (!editMeetingDraft) return;

        console.log('[DEBUG] Submitting meeting room edit:', editMeetingDraft);
        setProcessing(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                booking_date: editMeetingDraft.booking_date,
                start_time: editMeetingDraft.start_time,
                end_time: editMeetingDraft.end_time,
                meeting_title: editMeetingDraft.meeting_title
            };
            console.log('[DEBUG] PUT request payload:', payload);

            const response = await axios.put(`/api/meeting-rooms/bookings/${editMeetingDraft.id}`, payload);
            console.log('[DEBUG] PUT response:', response);

            setSuccess('Meeting room booking updated successfully');
            setEditMeetingDraft(null);
            await fetchMeetingRoomBookings();
        } catch (err) {
            console.error('[DEBUG] PUT request failed:', err);
            setError(err.response?.data?.error || 'Unable to update meeting room booking');
        } finally {
            setProcessing(false);
        }
    };

    const confirmedBookings = bookings.filter((booking) => booking.status === 'confirmed');

    const currentBookings = bookings.filter(
        (booking) => booking.status === 'confirmed' && booking.booking_date >= today
    );
    const completedBookings = bookings.filter(
        (booking) => booking.status === 'completed' || (booking.status === 'confirmed' && booking.booking_date < today)
    );

    // Meeting room bookings
    const currentMeetingRoomBookings = meetingRoomBookings.filter(
        (booking) => booking.booking_date >= today
    );
    const pastMeetingRoomBookings = meetingRoomBookings.filter(
        (booking) => booking.booking_date < today
    );

    return (
        <div className="booking-history-page">
            <HeaderBar user={user} onLogout={onLogout} />

            <div className="history-container">
                {(error || success) && (
                    <div className={`history-banner ${error ? 'error' : 'success'}`}>
                        {error || success}
                    </div>
                )}

                <section className="history-card">
                    <header className="history-header">
                        <div>
                            <h2>Current Bookings</h2>
                            <p>Manage, edit, or cancel your active bookings.</p>
                        </div>
                        <span className="badge">{currentBookings.length}</span>
                    </header>

                    {loading ? (
                        <div className="history-empty">Loading current bookings...</div>
                    ) : currentBookings.length === 0 ? (
                        <div className="history-empty">No active bookings yet.</div>
                    ) : (
                        <div className="table-scroll">
                            <table className="bookings-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Desk</th>
                                        <th>Zone</th>
                                        <th>Unit</th>
                                        <th>Time</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentBookings.map((booking) => (
                                        <tr key={booking.id}>
                                            <td>{formatDate(booking.booking_date)}</td>
                                            <td>{booking.desk_id}</td>
                                            <td>{booking.zone || '—'}</td>
                                            <td>{booking.unit || '—'}</td>
                                            <td>
                                                {booking.start_time && booking.end_time
                                                    ? `${booking.start_time} - ${booking.end_time}`
                                                    : 'Full day'}
                                            </td>
                                            <td>
                                                <div className="row-actions">
                                                    <button
                                                        type="button"
                                                        className="btn-secondary"
                                                        onClick={() => openEditModal(booking)}
                                                        disabled={processing}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-tertiary"
                                                        onClick={() => handleDeleteBooking(booking.id)}
                                                        disabled={processing}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* Meeting Room Bookings Section */}
                <section className="history-card meeting-room-section">
                    <header className="history-header">
                        <div>
                            <h2>🏢 Meeting Room Bookings</h2>
                            <p>Your meeting room reservations.</p>
                        </div>
                        <span className="badge">{currentMeetingRoomBookings.length}</span>
                    </header>

                    {loading ? (
                        <div className="history-empty">Loading meeting room bookings...</div>
                    ) : currentMeetingRoomBookings.length === 0 ? (
                        <div className="history-empty">No meeting room bookings.</div>
                    ) : (
                        <div className="table-scroll">
                            <table className="bookings-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Room</th>
                                        <th>Title</th>
                                        <th>Time</th>
                                        <th>Duration</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentMeetingRoomBookings.map((booking) => (
                                        <tr key={`mr-${booking.id}`}>
                                            <td>{formatDate(booking.booking_date)}</td>
                                            <td>
                                                <strong>{booking.room_name || booking.booth_id}</strong>
                                            </td>
                                            <td>{booking.meeting_title || '—'}</td>
                                            <td>{booking.start_time} - {booking.end_time}</td>
                                            <td>
                                                {(() => {
                                                    const [sh, sm] = booking.start_time.split(':').map(Number);
                                                    const [eh, em] = booking.end_time.split(':').map(Number);
                                                    const mins = (eh * 60 + em) - (sh * 60 + sm);
                                                    if (mins >= 60) {
                                                        const hrs = Math.floor(mins / 60);
                                                        const remMins = mins % 60;
                                                        return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs} hour${hrs > 1 ? 's' : ''}`;
                                                    }
                                                    return `${mins} min`;
                                                })()}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${booking.status || 'confirmed'}`}>
                                                    {booking.status === 'pending' ? 'Waitlist' : 'Confirmed'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="row-actions">
                                                    <button
                                                        type="button"
                                                        className="btn-secondary"
                                                        onClick={() => openEditMeetingModal(booking)}
                                                        disabled={processing}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn-tertiary"
                                                        onClick={() => handleCancelMeetingRoom(booking.id)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {completedBookings.length > 0 && (
                    <section className="history-card">
                        <header className="history-header">
                            <div>
                                <h2>Booking History</h2>
                                <p>Your completed and past bookings.</p>
                            </div>
                            <span className="badge">{completedBookings.length}</span>
                        </header>

                        <div className="table-scroll">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Desk</th>
                                        <th>Zone</th>
                                        <th>Unit</th>
                                        <th>Time</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {completedBookings.map((booking) => (
                                        <tr key={booking.id}>
                                            <td>{formatDate(booking.booking_date)}</td>
                                            <td>{booking.desk_id}</td>
                                            <td>{booking.zone || '—'}</td>
                                            <td>{booking.unit || '—'}</td>
                                            <td>
                                                {booking.start_time && booking.end_time
                                                    ? `${booking.start_time} - ${booking.end_time}`
                                                    : 'Full day'}
                                            </td>
                                            <td className={`status ${booking.status}`}>
                                                {booking.status === 'completed' ? 'Completed' : 'Past'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                )}
            </div>

            {editDraft && (
                <div className="modal" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Edit Booking</h3>
                            <button className="modal-close" onClick={() => setEditDraft(null)} aria-label="Close">
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleEditSubmit} className="modal-body">
                            <div className="form-group">
                                <label htmlFor="edit-booking-date">Booking Date</label>
                                <input
                                    id="edit-booking-date"
                                    type="date"
                                    value={editDraft.booking_date}
                                    min={today}
                                    onChange={(event) => setEditDraft((prev) => ({ ...prev, booking_date: event.target.value }))}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-booking-desk">Desk</label>
                                <select
                                    id="edit-booking-desk"
                                    value={editDraft.desk_id}
                                    onChange={(event) => setEditDraft((prev) => ({ ...prev, desk_id: event.target.value }))}
                                    required
                                >
                                    <option value={editDraft.desk_id}>
                                        {editDraft.desk_id} (Current)
                                    </option>
                                    {deskAvailability
                                        .filter(desk => desk.id !== editDraft.desk_id)
                                        .map((desk) => (
                                            <option
                                                key={desk.id}
                                                value={desk.id}
                                                disabled={!desk.available}
                                            >
                                                {desk.id} • {desk.monitor_setup || 'Monitor unknown'}
                                                {!desk.available ? ' (Occupied)' : ''}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-booking-slot">Time Slot</label>
                                <select
                                    id="edit-booking-slot"
                                    value={`${editDraft.start_time}-${editDraft.end_time}`}
                                    onChange={(event) => {
                                        const [start, end] = event.target.value.split('-');
                                        setEditDraft((prev) => ({ ...prev, start_time: start, end_time: end }));
                                    }}
                                >
                                    {DEFAULT_TIME_SLOTS.map((slot) => (
                                        <option key={slot.label} value={`${slot.start}-${slot.end}`}>
                                            {slot.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button type="submit" className="btn-primary" disabled={processing}>
                                    {processing ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-tertiary"
                                    onClick={() => setEditDraft(null)}
                                    disabled={processing}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {editMeetingDraft && (
                <div className="modal" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Edit Meeting - {editMeetingDraft.room_name}</h3>
                            <button className="modal-close" onClick={() => setEditMeetingDraft(null)} aria-label="Close">
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleEditMeetingSubmit} className="modal-body">
                            <div className="form-group">
                                <label htmlFor="edit-meeting-title">Meeting Title</label>
                                <input
                                    id="edit-meeting-title"
                                    type="text"
                                    value={editMeetingDraft.meeting_title}
                                    onChange={(e) => setEditMeetingDraft(prev => ({ ...prev, meeting_title: e.target.value }))}
                                    placeholder="Optional meeting title"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="edit-meeting-date">Date</label>
                                <input
                                    id="edit-meeting-date"
                                    type="date"
                                    value={editMeetingDraft.booking_date}
                                    min={today}
                                    onChange={(e) => setEditMeetingDraft(prev => ({ ...prev, booking_date: e.target.value }))}
                                    required
                                />
                            </div>
                            <div className="form-group-row">
                                <div className="form-group">
                                    <label htmlFor="edit-meeting-start">Start Time</label>
                                    <select
                                        id="edit-meeting-start"
                                        value={editMeetingDraft.start_time}
                                        onChange={(e) => {
                                            // Recalculate end time based on duration
                                            const newStart = e.target.value;
                                            const [h, m] = newStart.split(':').map(Number);
                                            const startMins = h * 60 + m;
                                            const endMins = startMins + editMeetingDraft.original_duration_minutes;
                                            const endH = Math.floor(endMins / 60);
                                            const endM = endMins % 60;
                                            const newEnd = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

                                            setEditMeetingDraft(prev => ({
                                                ...prev,
                                                start_time: newStart,
                                                end_time: newEnd
                                            }));
                                        }}
                                        required
                                    >
                                        {Array.from({ length: 20 }).map((_, i) => {
                                            // Generate 30 min slots from 07:00 to 16:30
                                            const mins = 7 * 60 + i * 30;
                                            const h = Math.floor(mins / 60);
                                            const m = mins % 60;
                                            const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                                            return <option key={time} value={time}>{time}</option>;
                                        })}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>End Time (Calculated)</label>
                                    <input
                                        type="text"
                                        value={editMeetingDraft.end_time}
                                        disabled
                                        className="input-disabled"
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="submit" className="btn-primary" disabled={processing}>
                                    {processing ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-tertiary"
                                    onClick={() => setEditMeetingDraft(null)}
                                    disabled={processing}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog for Cancelling Meeting Room */}
            {confirmDialog.show && (
                <div className="modal" role="dialog" aria-modal="true">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Confirm Cancellation</h3>
                            <button
                                className="modal-close"
                                onClick={() => setConfirmDialog({ show: false, bookingId: null, message: '' })}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>{confirmDialog.message}</p>
                        </div>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={confirmCancelMeetingRoom}
                                disabled={processing}
                            >
                                {processing ? 'Cancelling...' : 'Yes, Cancel Booking'}
                            </button>
                            <button
                                type="button"
                                className="btn-tertiary"
                                onClick={() => setConfirmDialog({ show: false, bookingId: null, message: '' })}
                                disabled={processing}
                            >
                                No, Keep It
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageBookingsPage;
